import React, { useState, useMemo } from 'react'
import { ArrowLeft, Check, Image, Calendar, MapPin, Search, X, Filter } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useApprovedGalleries, useAllGalleries } from '@/features/social/hooks'
import type { GalleryWithPhotos } from '@/lib/supabase'
import { format } from 'date-fns'

// Use the shared supabase types to avoid drift
type Gallery = GalleryWithPhotos

interface GalleryImageSelectorProps {
  onImagesSelected: (imageUrls: string[]) => void
  onClose: () => void
  maxImages?: number
  /** Called when a gallery is selected, passing the gallery ID for Caption Agent context */
  onGallerySelect?: (galleryId: number) => void
}

export default function GalleryImageSelector({
  onImagesSelected,
  onClose,
  maxImages = 10,
  onGallerySelect
}: GalleryImageSelectorProps) {
  const [selectedGallery, setSelectedGallery] = useState<Gallery | null>(null)
  const [selectedImages, setSelectedImages] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [regionFilter, setRegionFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState<string>('newest')
  const [galleryScope, setGalleryScope] = useState<'approved' | 'all'>('approved')
  const { data: approvedGalleries = [], isLoading: isLoadingApproved } = useApprovedGalleries()
  const { data: allGalleries = [], isLoading: isLoadingAll } = useAllGalleries({ enabled: galleryScope === 'all' })
  const galleries: Gallery[] = (galleryScope === 'all' ? (allGalleries || []) : (approvedGalleries || [])) as Gallery[]
  const isLoading = galleryScope === 'all' ? isLoadingAll : isLoadingApproved

  // Filter and search galleries
  const filteredGalleries = useMemo(() => {
    const filtered = galleries.filter((gallery: Gallery) => {
      // Search filter - check title, description, and job number
      const searchLower = searchTerm.toLowerCase()
      const matchesSearch = !searchTerm ||
        gallery.title?.toLowerCase().includes(searchLower) ||
        gallery.description?.toLowerCase().includes(searchLower) ||
        gallery.job_number?.toString().includes(searchTerm.replace('#', ''))

      // Region filter
      const matchesRegion = regionFilter === 'all' || gallery.franchise_region === regionFilter

      return matchesSearch && matchesRegion
    })

    // Sort galleries
    filtered.sort((a: Gallery, b: Gallery) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_on).getTime() - new Date(a.created_on).getTime()
        case 'oldest':
          return new Date(a.created_on).getTime() - new Date(b.created_on).getTime()
        case 'most_photos':
          return (b.gallery_photos?.length || 0) - (a.gallery_photos?.length || 0)
        case 'job_number':
          return (Number(a.job_number) || 0) - (Number(b.job_number) || 0)
        default:
          return 0
      }
    })

    return filtered
  }, [galleries, searchTerm, regionFilter, sortBy])

  // Get unique regions for filter
  const availableRegions = useMemo(() => {
    const regions = galleries.map((g: Gallery) => g.franchise_region).filter(Boolean)
    return [...new Set(regions)].sort()
  }, [galleries])

  const handleGallerySelect = (gallery: Gallery) => {
    setSelectedGallery(gallery)
    setSelectedImages([])
  }

  const handleImageToggle = (imageUrl: string) => {
    setSelectedImages(prev => {
      if (prev.includes(imageUrl)) {
        return prev.filter(url => url !== imageUrl)
      }
      if (prev.length >= maxImages) {
        return prev
      }
      return [...prev, imageUrl]
    })
  }

  const handleConfirmSelection = () => {
    onImagesSelected(selectedImages)
    // Notify parent of selected gallery for Caption Agent context
    if (selectedGallery && onGallerySelect) {
      onGallerySelect(selectedGallery.id)
    }
    onClose()
  }

  const getRegionBadgeColor = (region: string) => {
    const colors: Record<string, string> = {
      'HQ': 'bg-purple-500',
      'north_york': 'bg-blue-500',
      'london': 'bg-green-500',
      'toronto': 'bg-red-500',
      'hamilton': 'bg-orange-500',
      'mississauga': 'bg-pink-500',
      'brampton': 'bg-yellow-500',
      'scarborough': 'bg-indigo-500',
      'general': 'bg-gray-500'
    }
    return colors[region] || 'bg-gray-500'
  }

  const formatRegionName = (region: string) => {
    return region.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-32 h-32 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </Card>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-6xl max-h-[90vh] overflow-hidden">
        <CardHeader className="border-b space-y-4">
          <div className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-4">
              {selectedGallery && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedGallery(null)
                    setSelectedImages([])
                  }}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Galleries
                </Button>
              )}
              <CardTitle className="text-xl">
                {selectedGallery ? selectedGallery.title : 'Select Gallery'}
              </CardTitle>
            </div>
            <div className="flex items-center gap-2">
              {selectedImages.length > 0 && (
                <Badge variant="secondary" className="bg-green-100 text-green-800">
                  {selectedImages.length} / {maxImages} selected
                </Badge>
              )}
              <Button variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>

          {/* Search and Filter Bar - Only show when not in gallery view */}
          {!selectedGallery && (
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search galleries by title, description, or job number (e.g. 2696, #2696)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-10"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Filters */}
              <div className="flex gap-2">
                <Select value={galleryScope} onValueChange={(v) => setGalleryScope(v as 'approved' | 'all')}>
                  <SelectTrigger className="w-44">
                    <SelectValue placeholder="Gallery scope" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">Approved Only</SelectItem>
                    <SelectItem value="all">All Galleries</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={regionFilter} onValueChange={setRegionFilter}>
                  <SelectTrigger className="w-40">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Region" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Regions</SelectItem>
                    {availableRegions.map((region) => (
                      <SelectItem key={region} value={region}>
                        {formatRegionName(region)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="most_photos">Most Photos</SelectItem>
                    <SelectItem value="job_number">Job Number</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Results count and filters summary */}
          {!selectedGallery && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <div>
                Showing {filteredGalleries.length} of {galleries.length} galleries
                {searchTerm && (
                  <span className="ml-1">
                    for "<strong className="text-foreground">{searchTerm}</strong>"
                  </span>
                )}
              </div>
              {(searchTerm || regionFilter !== 'all' || galleryScope === 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('')
                    setRegionFilter('all')
                    setGalleryScope('approved')
                  }}
                  className="h-6 px-2 text-xs"
                >
                  Clear filters
                </Button>
              )}
            </div>
          )}
        </CardHeader>

        <ScrollArea className="h-[calc(90vh-8rem)]">
          <CardContent className="p-6">
            {!selectedGallery ? (
              // Gallery List View
              <div className="space-y-4">
                {filteredGalleries.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="max-w-md mx-auto space-y-4">
                      {searchTerm || regionFilter !== 'all' ? (
                        // No results for search/filter
                        <div className="space-y-4">
                          <div className="relative">
                            <Image className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                            <div className="absolute -top-1 -right-1 bg-amber-100 text-amber-600 rounded-full w-8 h-8 flex items-center justify-center">
                              <Search className="h-4 w-4" />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <h3 className="text-lg font-semibold text-gray-900">No galleries found</h3>
                            <p className="text-sm text-gray-600">
                              No galleries match your current search and filter criteria.
                            </p>
                          </div>

                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                            <h4 className="text-sm font-medium text-blue-900 mb-2">💡 Try:</h4>
                            <ul className="text-xs text-blue-800 space-y-1">
                              <li>• Different search terms or job numbers</li>
                              <li>• Clearing the region filter</li>
                              <li>• Checking spelling in your search</li>
                            </ul>
                          </div>

                          <Button
                            variant="outline"
                            onClick={() => {
                              setSearchTerm('')
                              setRegionFilter('all')
                            }}
                            className="flex items-center gap-2"
                          >
                            <X className="h-4 w-4" />
                            Clear All Filters
                          </Button>
                        </div>
                      ) : (
                        // No galleries at all
                        <div className="space-y-4">
                          <Image className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                          <div className="space-y-2">
                            <h3 className="text-lg font-semibold text-gray-900">No galleries available</h3>
                            <p className="text-sm text-gray-600">
                              There are no approved galleries available for image selection.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {filteredGalleries.map((gallery) => (
                      <Card
                        key={gallery.id}
                        className="group cursor-pointer hover:shadow-lg transition-all duration-200 border-2 hover:border-primary/50"
                        onClick={() => handleGallerySelect(gallery)}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-semibold text-lg text-gray-900 truncate">{gallery.title}</h3>
                                {gallery.job_number && (
                                  <Badge variant="outline" className="text-blue-700 bg-blue-50 border-blue-200 font-mono">
                                    #{gallery.job_number}
                                  </Badge>
                                )}
                              </div>
                              {gallery.description && (
                                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                  {gallery.description}
                                </p>
                              )}
                              <div className="flex items-center gap-4 text-sm">
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-4 w-4 text-gray-500" />
                                  <Badge
                                    variant="secondary"
                                    className={`${getRegionBadgeColor(gallery.franchise_region)} text-white font-medium`}
                                  >
                                    {formatRegionName(gallery.franchise_region)}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-1 text-gray-600">
                                  <Image className="h-4 w-4" />
                                  <span className="font-medium">{gallery.gallery_photos?.length || 0} photos</span>
                                </div>
                                <div className="flex items-center gap-1 text-gray-600">
                                  <Calendar className="h-4 w-4" />
                                  <span>{format(new Date(gallery.created_on), 'MMM d, yyyy')}</span>
                                </div>
                              </div>
                            </div>
                            {gallery.gallery_photos && gallery.gallery_photos.length > 0 && (
                              <div className="ml-6 flex-shrink-0">
                                <div className="grid grid-cols-3 gap-1 w-36">
                                  {gallery.gallery_photos.slice(0, 3).map((photo, idx) => (
                                    <img
                                      key={idx}
                                      src={photo.image_url}
                                      alt=""
                                      className="w-11 h-11 object-cover rounded-md group-hover:scale-105 transition-transform duration-200"
                                    />
                                  ))}
                                  {gallery.gallery_photos.length > 3 && (
                                    <div className="w-11 h-11 bg-gray-100 rounded-md flex items-center justify-center text-xs font-medium text-gray-600">
                                      +{gallery.gallery_photos.length - 3}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // Photo Grid View
              <div className="space-y-4">
                {selectedGallery.description && (
                  <p className="text-muted-foreground">{selectedGallery.description}</p>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {selectedGallery.gallery_photos?.map((photo) => {
                    const isSelected = selectedImages.includes(photo.image_url)
                    return (
                      <div
                        key={photo.id}
                        className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${isSelected ? 'border-primary' : 'border-transparent'
                          }`}
                        onClick={() => handleImageToggle(photo.image_url)}
                      >
                        <img
                          src={photo.image_url}
                          alt={photo.caption || ''}
                          className="w-full h-40 object-cover"
                        />
                        {photo.caption && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-2 text-xs">
                            {photo.caption}
                          </div>
                        )}
                        <div className={`absolute inset-0 transition-opacity ${isSelected ? 'bg-primary/20' : 'bg-black/0 group-hover:bg-black/20'
                          }`}>
                          {isSelected && (
                            <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                              <Check className="h-4 w-4" />
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>

                {selectedImages.length > 0 && (
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setSelectedImages([])}
                    >
                      Clear Selection
                    </Button>
                    <Button onClick={handleConfirmSelection}>
                      Add {selectedImages.length} Image{selectedImages.length !== 1 ? 's' : ''}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </ScrollArea>
      </Card>
    </div>
  )
}
